import React, { useState } from 'react';
import { Row, Col, Card, Button, Space, Tag, message, Empty, Badge } from 'antd';
import {
  DatabaseOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  StarOutlined,
} from '@ant-design/icons';
import TagFilter, { FilterValues } from './components/TagFilter';
import CaseList from './components/CaseList';
import CaseDetailView from './components/CaseDetailView';
import SimilarCases from './components/SimilarCases';
import type { AnnotationData } from '../../types';
import { mockAnnotationData } from '../../services/mockData';

// 将 mockAnnotationData 转换为数组
const allCases: AnnotationData[] = Object.values(mockAnnotationData);

const CaseSearch: React.FC = () => {
  const [filters, setFilters] = useState<FilterValues>({});
  const [filteredCases, setFilteredCases] = useState<AnnotationData[]>(allCases);
  const [selectedCase, setSelectedCase] = useState<AnnotationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');

  // 执行搜索
  const handleSearch = () => {
    setLoading(true);

    setTimeout(() => {
      let results = allCases;

      // 关键词搜索
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        results = results.filter(
          (c) =>
            c.docMeta.project_name.toLowerCase().includes(keyword) ||
            c.docMeta.case_id.toLowerCase().includes(keyword) ||
            c.questionDetails.some((q) =>
              q.question_summary.toLowerCase().includes(keyword)
            ) ||
            c.replyDetails.some((r) =>
              r.reply_reasoning.toLowerCase().includes(keyword)
            )
        );
      }

      // 标签筛选
      if (filters.orgBuyers?.length) {
        results = results.filter((c) =>
          filters.orgBuyers?.includes(c.docMeta.responder?.name || '')
        );
      }

      if (filters.orgBidders?.length) {
        results = results.filter((c) =>
          filters.orgBidders?.includes(c.docMeta.questioner?.name || '')
        );
      }

      if (filters.topics?.length) {
        results = results.filter((c) =>
          c.questionDetails.some((q) => filters.topics?.includes(q.topic))
        );
      }

      if (filters.riskPoints?.length) {
        results = results.filter((c) =>
          c.questionDetails.some((q) =>
            filters.riskPoints?.some((rp) =>
              q.question_summary.includes(rp) || q.question_detail.includes(rp)
            )
          )
        );
      }

      if (filters.strategyKeywords?.length) {
        results = results.filter((c) =>
          c.replyDetails.some((r) =>
            filters.strategyKeywords?.some((sk) =>
              r.reply_reasoning.includes(sk) || r.reply_conclusion.includes(sk)
            )
          )
        );
      }

      if (filters.replyPositions?.length) {
        results = results.filter((c) =>
          c.replyDetails.some((r) =>
            filters.replyPositions?.includes(r.reply_position)
          )
        );
      }

      if (filters.laws?.length) {
        results = results.filter((c) =>
          c.replyDetails.some((r) =>
            r.laws_regulations_cited?.some((law) =>
              filters.laws?.includes(law.name)
            )
          )
        );
      }

      setFilteredCases(results);
      setLoading(false);

      message.success(`找到 ${results.length} 个匹配案例`);
    }, 300);
  };

  // 重置筛选
  const handleReset = () => {
    setFilters({});
    setFilteredCases(allCases);
    setSelectedCase(null);
  };

  // 收藏案例
  const handleFavoriteCase = (caseId: string) => {
    setFavorites((prev) => {
      if (prev.includes(caseId)) {
        message.success('已取消收藏');
        return prev.filter((id) => id !== caseId);
      } else {
        message.success('已收藏');
        return [...prev, caseId];
      }
    });
  };

  return (
    <div className="min-h-[calc(100vh-112px)] bg-gray-100">
      {/* 页面标题 */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold">案例查询</h1>
        <Space>
          <Badge count={filteredCases.length} showZero color="#1890ff">
            <Tag color="blue">搜索结果</Tag>
          </Badge>
          <Badge count={allCases.length} showZero color="#52c41a">
            <Tag color="green">总案例</Tag>
          </Badge>
        </Space>
      </div>

      {/* ========== 搜索区 ========== */}
      <div className="px-6 pb-4">
        <Card className="shadow-sm" styles={{ body: { padding: 0 } }}>
          <TagFilter
            values={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            onReset={handleReset}
          />
        </Card>
      </div>

      {/* ========== 结果展示区 ========== */}
      <div className="px-6 pb-6">
        <Row gutter={16}>
          {/* 左侧：案例列表 */}
          <Col span={7}>
            <Card
              title={
                <div className="flex items-center justify-between">
                  <Space>
                    <DatabaseOutlined />
                    <span>案例列表</span>
                    <Tag color="blue">{filteredCases.length}</Tag>
                  </Space>
                  <Space>
                    <Button
                      type={viewMode === 'list' ? 'primary' : 'default'}
                     
                      icon={<UnorderedListOutlined />}
                      onClick={() => setViewMode('list')}
                    />
                    <Button
                      type={viewMode === 'card' ? 'primary' : 'default'}
                     
                      icon={<AppstoreOutlined />}
                      onClick={() => setViewMode('card')}
                    />
                  </Space>
                </div>
              }
              styles={{ body: { padding: '12px' } }}
            >
              <CaseList
                cases={filteredCases}
                selectedCaseId={selectedCase?.docMeta.case_id}
                onSelectCase={setSelectedCase}
                loading={loading}
                viewMode={viewMode}
              />
            </Card>
          </Col>

          {/* 右侧：案例详情 */}
          <Col span={17}>
            {selectedCase ? (
              <Row gutter={16}>
                {/* 案例详情 */}
                <Col span={16}>
                  <CaseDetailView
                    caseData={selectedCase}
                    onCopyTemplate={(content) => {
                      message.success('模板已复制');
                    }}
                    onFavorite={(content) => {
                      handleFavoriteCase(selectedCase.docMeta.case_id);
                    }}
                  />
                </Col>

                {/* 相似案例 + 收藏 */}
                <Col span={8}>
                  <SimilarCases
                    currentCaseId={selectedCase.docMeta.case_id}
                    onViewCase={(caseId) => {
                      const caseData = allCases.find((c) => c.docMeta.case_id === caseId);
                      if (caseData) {
                        setSelectedCase(caseData);
                      }
                    }}
                  />

                  {/* 我的收藏 */}
                  <Card
                    title={
                      <div className="flex items-center gap-2">
                        <StarOutlined className="text-yellow-500" />
                        <span>我的收藏</span>
                        <Tag>{favorites.length}</Tag>
                      </div>
                    }
                   
                  >
                    {favorites.length === 0 ? (
                      <Empty description="暂无收藏" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                      <Space direction="vertical" className="w-full">
                        {favorites.map((caseId) => {
                          const caseData = allCases.find((c) => c.docMeta.case_id === caseId);
                          return caseData ? (
                            <Card
                              key={caseId}
                             
                              className="cursor-pointer hover:shadow-md"
                              onClick={() => setSelectedCase(caseData)}
                            >
                              <div className="font-mono text-xs text-gray-500">
                                {caseId}
                              </div>
                              <div className="text-sm truncate">
                                {caseData.docMeta.project_name}
                              </div>
                            </Card>
                          ) : null;
                        })}
                      </Space>
                    )}
                  </Card>
                </Col>
              </Row>
            ) : (
              <Card className="h-full flex items-center justify-center" styles={{ body: { minHeight: 600 } }}>
                <Empty
                  description="请选择案例查看详情"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </Card>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CaseSearch;
